import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CreditCard, FileText, AlertCircle } from "lucide-react";

// Public endpoints (no cookies needed)
const publicApi = axios.create({ baseURL: API_BASE });

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

export default function FamilyInvoice() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paidNow, setPaidNow] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`/public/invoices/${token}`);
      setInvoice(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invoice link is invalid or expired");
    } finally {
      setLoading(false);
    }
  };

  // First load
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Stripe redirected back with ?session_id=… — poll the status endpoint
  // until paid (or 10 attempts). On paid, refresh the invoice.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      setPolling(true);
      try {
        const { data } = await publicApi.get(`/public/invoices/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaidNow(true);
          await load();
          setPolling(false);
          // Strip the session_id from the URL so a refresh doesn't re-poll
          const next = new URLSearchParams(params);
          next.delete("session_id");
          next.set("paid", "1");
          setParams(next, { replace: true });
          return;
        }
      } catch (err) {
        // Stripe sometimes takes a moment to surface the session — keep polling
        console.debug("Invoice poll attempt failed (will retry):", err?.message);
      }
      if (attempts >= 10) {
        setPolling(false);
        return;
      }
      setTimeout(poll, 1500);
    };

    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await publicApi.post(`/public/invoices/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("Stripe did not return a redirect URL");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading invoice…
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-rose-500 mb-3" />
          <h1 className="font-heading text-xl font-semibold">Invoice not found</h1>
          <p className="mt-2 text-sm text-clinical-subtext">{error || "Please contact the agency for a fresh link."}</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice.status === "paid" || paidNow;
  const branding = invoice.agency?.branding || {};

  return (
    <div className="min-h-screen bg-clinical-bg/40 py-10 px-4" data-testid="family-invoice-page">
      <div className="max-w-2xl mx-auto">
        {/* Branded header */}
        <div className="mb-4 flex items-center gap-2 text-clinical-subtext text-xs">
          <span
            className="inline-block w-7 h-7 rounded-md text-white text-center leading-7 font-semibold"
            style={{ background: branding.primary_color || "#004AE6" }}
          >
            {(invoice.agency?.name || "A").charAt(0)}
          </span>
          <span className="font-medium text-clinical-text">{invoice.agency?.name || "Care provider"}</span>
        </div>

        {/* Card */}
        <div className="card-surface p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-eyebrow flex items-center gap-1.5">
                <FileText className="h-3 w-3" /> Invoice
              </div>
              <h1 className="mt-1 font-heading text-2xl font-semibold">{invoice.number}</h1>
            </div>
            {isPaid ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-medium"
                data-testid="family-invoice-paid-badge"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Paid
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 text-xs font-medium">
                Due {fmtDate(invoice.due_date)}
              </span>
            )}
          </div>

          {paidNow && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="family-invoice-success-banner">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Thank you — your payment was received. The agency has been notified.
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="label-eyebrow">Care for</div>
              <div className="mt-0.5 font-medium">{invoice.client_name}</div>
            </div>
            <div>
              <div className="label-eyebrow">Service period</div>
              <div className="mt-0.5">
                {fmtDate(invoice.period_start)} – {fmtDate(invoice.period_end)}
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="mt-6 border-t border-clinical-border pt-4 space-y-2">
            {(invoice.line_items || []).map((li, i) => (
              <div key={`line-${i}-${li.date || ""}-${li.amount || 0}`} className="flex items-baseline justify-between gap-3 text-sm" data-testid={`invoice-line-${i}`}>
                <div className="flex-1">
                  <div className="font-medium">{li.description || "Care visit"}</div>
                  <div className="text-xs text-clinical-subtext">
                    {fmtDate(li.date)} · {Number(li.hours).toFixed(2)}h @ {fmtMoney(li.rate)}/h
                  </div>
                </div>
                <div className="font-medium tabular-nums">{fmtMoney(li.amount)}</div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mt-4 border-t border-clinical-border pt-4 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-clinical-subtext">Subtotal</span>
              <span className="tabular-nums">{fmtMoney(invoice.subtotal)}</span>
            </div>
            {invoice.tax_amount > 0 && (
              <div className="flex justify-between">
                <span className="text-clinical-subtext">Tax</span>
                <span className="tabular-nums">{fmtMoney(invoice.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-1 border-t border-clinical-border mt-2 text-lg font-semibold">
              <span>Total due</span>
              <span className="tabular-nums" data-testid="family-invoice-total">{fmtMoney(invoice.total)}</span>
            </div>
          </div>

          {/* Pay button */}
          {!isPaid && (
            <button
              type="button"
              onClick={pay}
              disabled={paying || polling}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl text-white font-semibold py-3 px-4 shadow-sm disabled:opacity-60"
              style={{ background: branding.primary_color || "#004AE6" }}
              data-testid="family-pay-button"
            >
              {polling ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Confirming payment…
                </>
              ) : paying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Redirecting to Stripe…
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4" /> Pay {fmtMoney(invoice.total)}
                </>
              )}
            </button>
          )}

          {invoice.notes && (
            <div className="mt-6 border-t border-clinical-border pt-4">
              <div className="label-eyebrow mb-1">Note from {invoice.agency?.name || "the agency"}</div>
              <p className="text-sm text-clinical-text whitespace-pre-line">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* Agency footer */}
        <div className="mt-6 text-center text-xs text-clinical-subtext">
          Questions? Contact {invoice.agency?.name || "the agency"}
          {invoice.agency?.email ? ` at ${invoice.agency.email}` : ""}.
          <div className="mt-1">Powered by Afyanex Care</div>
        </div>
      </div>
    </div>
  );
}
